import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Chrome,
  Download,
  ThumbsUp,
  ThumbsDown,
  Camera,
  Users,
  MapPin,
  Clock,
  CheckCircle,
  AlertTriangle,
  Share2,
  Globe,
  Upload
} from 'lucide-react';

interface CrowdReport {
  id: string;
  zone: string;
  confirmed: number;
  denied: number;
  consensus: number;
  lastUpdated: string;
  issue: string;
}

interface UserConfirmation {
  userId: string;
  zone: string;
  confirmed: boolean;
  timestamp: string;
  photo?: string;
}

const BrowserExtension: React.FC = () => {
  const [isInstalled, setIsInstalled] = useState(true); // Simulate installed
  const [showPopup, setShowPopup] = useState(false);
  const [selectedZone, setSelectedZone] = useState('Zone A');
  const [userConfirmed, setUserConfirmed] = useState<boolean | null>(null);
  const [photoUploaded, setPhotoUploaded] = useState(false);
  const [crowdData, setCrowdData] = useState<CrowdReport[]>([
    {
      id: '1',
      zone: 'Zone A',
      confirmed: 247,
      denied: 15,
      consensus: 94,
      lastUpdated: 'Just now',
      issue: 'Severe flooding reported'
    },
    {
      id: '2',
      zone: 'Zone B',
      confirmed: 89,
      denied: 11,
      consensus: 89,
      lastUpdated: '2 min ago',
      issue: 'Fire in market area'
    },
    {
      id: '3',
      zone: 'Zone C',
      confirmed: 34,
      denied: 23,
      consensus: 60,
      lastUpdated: '5 min ago',
      issue: 'Building collapse'
    }
  ]);

  const [recentConfirmations, setRecentConfirmations] = useState<UserConfirmation[]>([
    {
      userId: 'user_3847',
      zone: 'Zone A',
      confirmed: true,
      timestamp: '30s ago'
    },
    {
      userId: 'user_9210',
      zone: 'Zone A',
      confirmed: true,
      timestamp: '45s ago',
      photo: '📸'
    },
    {
      userId: 'user_4521',
      zone: 'Zone B',
      confirmed: false,
      timestamp: '1m ago'
    }
  ]);

  const handleConfirm = (confirmed: boolean) => {
    setUserConfirmed(confirmed);
    
    // Update crowd data
    setCrowdData(prev => prev.map(zone => 
      zone.zone === selectedZone
        ? {
            ...zone,
            confirmed: confirmed ? zone.confirmed + 1 : zone.confirmed,
            denied: !confirmed ? zone.denied + 1 : zone.denied,
            consensus: Math.round(((confirmed ? zone.confirmed + 1 : zone.confirmed) / 
                      (zone.confirmed + zone.denied + 1)) * 100)
          }
        : zone
    ));

    // Add to recent confirmations
    setRecentConfirmations(prev => [{
      userId: `user_${Math.floor(Math.random() * 10000)}`,
      zone: selectedZone,
      confirmed: confirmed,
      timestamp: 'Just now',
      photo: photoUploaded ? '📸' : undefined
    }, ...prev.slice(0, 4)]);
  };

  const handlePhotoUpload = () => {
    setPhotoUploaded(true);
    // Simulate photo upload
    setTimeout(() => {
      setPhotoUploaded(false);
    }, 2000);
  };

  const getConsensusColor = (consensus: number) => {
    if (consensus >= 80) return 'text-green-500 border-green-500 bg-green-500/10';
    if (consensus >= 60) return 'text-yellow-500 border-yellow-500 bg-yellow-500/10';
    return 'text-red-500 border-red-500 bg-red-500/10';
  };

  return (
    <div className="bg-gray-900/50 backdrop-blur-xl border border-cyan-500/30 rounded-xl p-6 holographic-card">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Chrome className="text-blue-500 animate-pulse" />
            BROWSER EXTENSION SIMULATOR
          </h2>
          <p className="text-xs text-gray-400 mt-1">Crowd-sourced disaster confirmation</p>
        </div>

        {/* Install Button */}
        {!isInstalled && (
          <button className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-2 rounded-lg text-sm">
            <Download size={16} />
            Install Extension
          </button>
        )}
      </div>

      {/* Extension Popup Simulator */}
      <motion.div
        className="border border-cyan-500/30 rounded-xl overflow-hidden mb-6"
        style={{ boxShadow: '0 0 30px rgba(0,255,255,0.2)' }}
      >
        {/* Popup Header */}
        <div className="bg-gradient-to-r from-cyan-600 to-blue-600 p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Chrome size={20} className="text-white" />
              <span className="font-bold">VECTOR Alert Extension</span>
            </div>
            <span className="text-xs bg-white/20 px-2 py-1 rounded">Active</span>
          </div>
        </div>

        {/* Popup Content */}
        <div className="p-4 bg-black/50">
          {/* Zone Selection */}
          <div className="mb-4">
            <label className="text-xs text-gray-400 mb-1 block">Select Zone</label>
            <select
              value={selectedZone}
              onChange={(e) => setSelectedZone(e.target.value)}
              className="w-full bg-black/50 border border-cyan-500/30 rounded-lg px-3 py-2 text-sm"
            >
              <option>Zone A - Critical</option>
              <option>Zone B - Warning</option>
              <option>Zone C - Monitor</option>
            </select>
          </div>

          {/* Alert Message */}
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg mb-4">
            <div className="flex items-center gap-2 text-red-500 mb-1">
              <AlertTriangle size={16} />
              <span className="text-sm font-bold">Confirm flooding in {selectedZone}?</span>
            </div>
            <p className="text-xs text-gray-400">
              Your confirmation helps rescue teams verify the situation
            </p>
          </div>

          {/* Confirmation Buttons */}
          <div className="flex gap-3 mb-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleConfirm(true)}
              className={`flex-1 py-3 rounded-lg font-bold flex items-center justify-center gap-2 transition-all ${
                userConfirmed === true 
                  ? 'bg-green-600 text-white' 
                  : 'bg-green-600/20 text-green-500 border border-green-500/30'
              }`}
            >
              <ThumbsUp size={16} />
              YES
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleConfirm(false)}
              className={`flex-1 py-3 rounded-lg font-bold flex items-center justify-center gap-2 transition-all ${
                userConfirmed === false 
                  ? 'bg-red-600 text-white' 
                  : 'bg-red-600/20 text-red-500 border border-red-500/30'
              }`}
            >
              <ThumbsDown size={16} />
              NO
            </motion.button>
          </div>

          {/* Photo Upload */}
          <div className="mb-4">
            <button
              onClick={handlePhotoUpload}
              className="w-full bg-purple-600/20 hover:bg-purple-600/30 text-purple-500 border border-purple-500/30 rounded-lg py-3 flex items-center justify-center gap-2 text-sm font-bold"
            >
              {photoUploaded ? (
                <>
                  <Upload size={16} className="animate-spin" />
                  UPLOADING...
                </>
              ) : (
                <>
                  <Camera size={16} />
                  UPLOAD PHOTO
                </>
              )}
            </button>
          </div>

          {/* Crowd Consensus */}
          {crowdData.find(z => z.zone === selectedZone) && (
            <div className={`p-3 rounded-lg border ${getConsensusColor(
              crowdData.find(z => z.zone === selectedZone)?.consensus || 0
            )}`}>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm">Crowd Consensus</span>
                <span className="text-2xl font-bold">
                  {crowdData.find(z => z.zone === selectedZone)?.consensus}%
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span>✅ {crowdData.find(z => z.zone === selectedZone)?.confirmed} confirmed</span>
                <span>❌ {crowdData.find(z => z.zone === selectedZone)?.denied} denied</span>
              </div>
              <div className="w-full bg-gray-700 h-1 rounded-full mt-2">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${crowdData.find(z => z.zone === selectedZone)?.consensus}%` }}
                  className={`h-1 rounded-full ${
                    (crowdData.find(z => z.zone === selectedZone)?.consensus || 0) >= 80 
                      ? 'bg-green-500' 
                      : (crowdData.find(z => z.zone === selectedZone)?.consensus || 0) >= 60
                      ? 'bg-yellow-500'
                      : 'bg-red-500'
                  }`}
                />
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Zone-wise Consensus Stats */}
      <div className="mb-6">
        <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
          <Users size={16} className="text-cyan-500" />
          Live Consensus by Zone
        </h3>
        <div className="grid grid-cols-3 gap-3">
          {crowdData.map((zone) => (
            <motion.div
              key={zone.id}
              whileHover={{ scale: 1.02 }}
              className="p-3 bg-black/30 rounded-lg border border-cyan-500/30"
            >
              <div className="flex justify-between items-start mb-2">
                <span className="font-bold">{zone.zone}</span>
                <span className={`text-xs px-2 py-1 rounded-full ${getConsensusColor(zone.consensus)}`}>
                  {zone.consensus}%
                </span>
              </div>
              <p className="text-xs text-gray-400 mb-2">{zone.issue}</p>
              <div className="flex justify-between text-xs">
                <span className="text-green-500">✓ {zone.confirmed}</span>
                <span className="text-red-500">✗ {zone.denied}</span>
              </div>
              <div className="flex items-center gap-1 mt-2 text-xs text-gray-400">
                <Clock size={10} />
                {zone.lastUpdated}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Recent Confirmations Feed */}
      <div>
        <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
          <Globe size={16} className="text-purple-500" />
          Recent Confirmations
        </h3>
        <div className="space-y-2">
          <AnimatePresence>
            {recentConfirmations.map((conf, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="flex items-center justify-between p-2 bg-black/30 rounded-lg border border-gray-700"
              >
                <div className="flex items-center gap-2">
                  {conf.confirmed ? (
                    <ThumbsUp size={14} className="text-green-500" />
                  ) : (
                    <ThumbsDown size={14} className="text-red-500" />
                  )}
                  <span className="text-xs font-mono">{conf.userId}</span>
                  <span className="text-xs text-gray-400">in {conf.zone}</span>
                  {conf.photo && <Camera size={12} className="text-purple-500" />}
                </div>
                <span className="text-xs text-gray-400">{conf.timestamp}</span>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      {/* Extension Stats */}
      <div className="mt-6 grid grid-cols-3 gap-4 text-center pt-4 border-t border-gray-700/50">
        <div>
          <div className="text-2xl font-bold text-cyan-500">1,847</div>
          <div className="text-xs text-gray-400">Active Users</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-green-500">94%</div>
          <div className="text-xs text-gray-400">Consensus Rate</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-purple-500">2.4k</div>
          <div className="text-xs text-gray-400">Photos Shared</div>
        </div>
      </div>
    </div>
  );
};

export default BrowserExtension;